import type { Translate } from '../../lib/apiErrorMessage';
import type { FirstFilingFact, MyPeriodFacts } from '../../services/complianceApi';
import type { Moment } from './moments';

/** "2026-09" → "September" / "Septiembre" (sentence-initial, so capitalized in every locale). */
export function monthName(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number);
  const name = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, 1)));
  return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
}

/**
 * Spec § 3.1, the hooray: "September is done. 23 filings, $14,212.40 remitted, all on time."
 *
 * Pure. Null unless the server PROVED the period complete. Each claim appears only when its own
 * fact is proven: `remitted` is null on the server when any filing lacks a recorded payment, and
 * then the "$X remitted" clause is dropped — never estimated; "all on time" only when true.
 */
export function periodCompleteMoment(facts: MyPeriodFacts, t: Translate, locale: string, onTimeStreak = 0): Moment | null {
  if (!facts.complete || facts.owed <= 0 || facts.filed !== facts.owed) return null;

  const parts = [t('moments.periodComplete.filings', { count: facts.owed })];
  if (facts.remitted != null) {
    const amount = facts.remitted.toLocaleString(locale, { style: 'currency', currency: 'USD' });
    parts.push(t('moments.periodComplete.remitted', { amount }));
  }
  if (facts.allOnTime === true) {
    parts.push(t('moments.periodComplete.onTime'));
    // § 3.4, folded into the one banner rather than a second one competing with it.
    if (onTimeStreak >= 2) parts.push(t('moments.periodComplete.streak', { count: onTimeStreak }));
  }

  return {
    id: `period-complete:${facts.period}`,
    kind: 'period-complete',
    title: t('moments.periodComplete.title', { month: monthName(facts.period, locale) }),
    detail: `${parts.join(', ')}.`,
    confetti: true,
  };
}

/**
 * Spec § 3.5, quiet: "First filing for Bitmantitle Incorporated is in." One per fact the SERVER proved
 * (a filed period containing the client's recorded start, `AccomplishmentFacts.FirstFilingsFor`); the
 * id is the server's, so each is seen once. No confetti: it's a nod, not a party.
 */
export function firstFilingMoments(facts: readonly FirstFilingFact[] | undefined, t: Translate): Moment[] {
  return (facts ?? []).map((f): Moment => ({
    id: f.momentId,
    kind: 'first-filing',
    title: t('moments.firstFiling.title', { client: f.clientName }),
    detail: t('moments.firstFiling.detail', { period: f.periodLabel }),
    confetti: false,
  }));
}
